/**
 * Utilities for formatting schedule configurations for display
 */

import { formatTimeBasedSchedule, parseCronExpression } from './cronBuilder';
import type { ScheduleConfiguration } from './types';

/**
 * Format a schedule configuration for display in the UI
 */
export function formatSchedule(schedule: ScheduleConfiguration | undefined): string {
  if (!schedule) {
    return 'Not scheduled';
  }

  if (!schedule.enabled) {
    return 'Disabled';
  }

  // Interval-based schedule
  if (schedule.interval) {
    const { value, unit } = schedule.interval;
    const unitStr = value === 1 ? unit.slice(0, -1) : unit; // Singular/plural
    return `Every ${value} ${unitStr}`;
  }

  // Cron-based schedule (time-of-day)
  if (schedule.cronExpression) {
    const parsed = parseCronExpression(schedule.cronExpression);
    if (parsed) {
      return formatTimeBasedSchedule(parsed);
    }
    // Fall back to showing raw cron if we can't parse it
    return `Cron: ${schedule.cronExpression}`;
  }

  return 'Schedule configured';
}

/**
 * Format a schedule with timezone information
 */
export function formatScheduleWithTimezone(schedule: ScheduleConfiguration | undefined): string {
  const scheduleStr = formatSchedule(schedule);

  if (!schedule || !schedule.enabled || scheduleStr === 'Not scheduled' || scheduleStr === 'Disabled') {
    return scheduleStr;
  }

  // Add timezone info
  const timezone = schedule.timezone || 'UTC';
  const tzAbbr = getTimezoneAbbreviation(timezone);

  return `${scheduleStr} ${tzAbbr}`;
}

/**
 * Get a short timezone abbreviation for display
 */
function getTimezoneAbbreviation(timezone: string): string {
  const abbreviations: Record<string, string> = {
    'UTC': 'UTC',
    'America/New_York': 'EST/EDT',
    'America/Chicago': 'CST/CDT',
    'America/Denver': 'MST/MDT',
    'America/Los_Angeles': 'PST/PDT',
    'Europe/London': 'GMT/BST',
    'Europe/Paris': 'CET/CEST',
    'Asia/Tokyo': 'JST',
    'Australia/Sydney': 'AEDT/AEST',
  };

  return abbreviations[timezone] || timezone;
}

/**
 * Format a date/time for display with timezone
 */
export function formatDateTime(
  isoString: string | undefined,
  timezone?: string,
  includeSeconds: boolean = false
): string {
  if (!isoString) {
    return 'Never';
  }

  try {
    const date = new Date(isoString);

    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      ...(includeSeconds && { second: '2-digit' }),
      timeZone: timezone || 'UTC',
      timeZoneName: 'short',
    };

    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

/**
 * Format a relative time (e.g., "in 2 hours", "3 days ago")
 */
export function formatRelativeTime(isoString: string | undefined): string {
  if (!isoString) {
    return 'Never';
  }

  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffSec = Math.abs(Math.floor(diffMs / 1000));
    const isFuture = diffMs > 0;

    if (diffSec < 60) {
      return isFuture ? 'In less than a minute' : 'Just now';
    }

    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) {
      return isFuture
        ? `In ${diffMin} minute${diffMin !== 1 ? 's' : ''}`
        : `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    }

    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) {
      return isFuture
        ? `In ${diffHour} hour${diffHour !== 1 ? 's' : ''}`
        : `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    }

    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 7) {
      return isFuture
        ? `In ${diffDay} day${diffDay !== 1 ? 's' : ''}`
        : `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    }

    const diffWeek = Math.floor(diffDay / 7);
    if (diffWeek < 4) {
      return isFuture
        ? `In ${diffWeek} week${diffWeek !== 1 ? 's' : ''}`
        : `${diffWeek} week${diffWeek !== 1 ? 's' : ''} ago`;
    }

    const diffMonth = Math.floor(diffDay / 30);
    return isFuture
      ? `In ${diffMonth} month${diffMonth !== 1 ? 's' : ''}`
      : `${diffMonth} month${diffMonth !== 1 ? 's' : ''} ago`;
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Invalid date';
  }
}

/**
 * Format both absolute and relative time for display
 */
export function formatDateTimeWithRelative(
  isoString: string | undefined,
  timezone?: string
): string {
  const absolute = formatDateTime(isoString, timezone);
  const relative = formatRelativeTime(isoString);

  if (absolute === 'Never' || absolute === 'Invalid date') {
    return absolute;
  }

  return `${absolute} (${relative})`;
}
